import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { Trello } from "../../../external/trello-pu__core/trello-iframe/trello";
import { TValidCardAttachment } from ".";
import { css } from "@emotion/react";
import {
  CardNotFoundAPICallError,
  UnauthorizedCardPermissionRequestedAPICallError,
} from "../../../external/trello-pu__core/trello-iframe/_use-rest-api/use-rest-api-client";
import baseStyles from "../../styles/base-styles";
import fontStylesNew from "../../styles/font-styles-new";
import trelloColors from "../../styles/trello-colors";
import {
  isLinkToTrelloCard,
  extractTrelloCardShortLink,
} from "../../utils/attachment-link-helper";
import ConditionalRender from "../shared/components/conditional-renderer";
import useBoardTargets from "../shared/hooks/use-board-targets";
import { useTrelloPowerUpContext } from "../../../external/trello-pu__core/trello-iframe/context";
import CardsGroupsList from "./cards-groups-list";
import Summary from "./summary";

type TCardsAttachmentsState = {
  validCards: TValidCardAttachment[];
  removedCardsAttachments: Trello.Attachment[];
  unauthorizedToViewCards: Trello.Attachment[];
};

function AttachmentsOverview() {
  const [currentCard, setCurrentCard] = useState<Pick<
    Trello.Card,
    "id" | "shortLink" | "url"
  > | null>(null);
  const [cards, setCards] = useState<TCardsAttachmentsState>({
    validCards: [],
    removedCardsAttachments: [],
    unauthorizedToViewCards: [],
  });
  // const trelloApiMethods = useRestApi();
  const [boardTargets, , , refreshTargets] = useBoardTargets();
  const [errors, setErrors] = useState<boolean>(false);
  const { trelloIFrame, restApi } = useTrelloPowerUpContext();

  const fetchCardsFromAttachments = useCallback(
    async (cardAttachments: Trello.Attachment[]) => {
      // trackEvent("ReadyToFetchCardsFromAttachments", {
      //   category: "BulkCardsAttachments",
      //   cardAttachmentsCount: cardAttachments.length,
      // });
      const idsCards = cardAttachments.map(
        (ca) => extractTrelloCardShortLink(ca.url) as string
      );
      const results = await restApi.fetchCardsInBatches(idsCards, true, true, [
        CardNotFoundAPICallError,
        UnauthorizedCardPermissionRequestedAPICallError,
      ]);
      if (results.idsWithError.length > 0) {
        setErrors(true);
      }
      const validCards = results.valid.map((c) => ({
        card: c,
        idAttachment: cardAttachments.find(
          (ca) => extractTrelloCardShortLink(ca.url) == c.shortLink
        )?.id as string,
        targetList: null,
      }));
      const removedCardsAttachments = results.idsRemoved.map((c) =>
        cardAttachments.find((ca) => extractTrelloCardShortLink(ca.url) == c)
      ) as Trello.Attachment[];
      const unauthorizedToViewCards = results.idsUnauthorized.map((c) =>
        cardAttachments.find((ca) => extractTrelloCardShortLink(ca.url) == c)
      ) as Trello.Attachment[];

      return { validCards, removedCardsAttachments, unauthorizedToViewCards };
    },
    [restApi]
  );

  const refresh = useCallback(() => {
    return restApi
      .fetchAttachmentsForCard(trelloIFrame.getContext().card as string)
      .then((attachments) =>
        attachments.filter((a) => isLinkToTrelloCard(a.url))
      )
      .then((cardAttachments) => fetchCardsFromAttachments(cardAttachments))
      .then(setCards)
      .catch(() => setErrors(true));
  }, [restApi, trelloIFrame, fetchCardsFromAttachments]);

  const getTargetListForCard = useCallback(
    (card: Trello.Card): TValidCardAttachment["targetList"] => {
      const targetsForCardsBoard = boardTargets.find(
        (t) => t.board.id == card.idBoard
      );
      if (
        targetsForCardsBoard &&
        targetsForCardsBoard.listsDone.map((l) => l.id).includes(card.idList)
      ) {
        return "done";
      } else if (
        targetsForCardsBoard &&
        targetsForCardsBoard.listsWIP.map((l) => l.id).includes(card.idList)
      ) {
        return "wip";
      } else {
        return "none";
      }
    },
    [boardTargets]
  );

  useEffect(() => {
    restApi
      .fetchCard(trelloIFrame.getContext().card as string)
      .then(setCurrentCard);
    refresh();
  }, [trelloIFrame, restApi, refresh]);

  useEffect(() => {
    (cards.validCards.length > 0 ||
      cards.removedCardsAttachments.length > 0 ||
      errors) &&
      trelloIFrame.sizeTo("#attachment-section-root");
  }, [cards, errors, trelloIFrame]);

  useEffect(() => {
    if (cards.validCards.some((c) => c.targetList === null)) {
      // null - happens only right after fetchning
      const updatedValidCards = cards.validCards.map((c) => ({
        ...c,
        targetList: getTargetListForCard(c.card),
      }));
      setCards((prev) => ({ ...prev, validCards: updatedValidCards }));
    }
  }, [boardTargets, cards, getTargetListForCard]);

  trelloIFrame.render(
    debounce(() => {
      if (cards.validCards.length > 0) {
        refresh();
        refreshTargets();
      }
    }, 500)
  );

  function getTargetsStats() {
    const relevantCards = cards.validCards.filter((c) =>
      boardTargets.some((bt) => bt.board.id == c.card.idBoard)
    );
    return {
      all: relevantCards.length,
      done: relevantCards.filter((c) => c.targetList == "done").length,
      wip: relevantCards.filter((c) => c.targetList == "wip").length,
    };
  }

  function onConnectCardsRequestHandler(idTargetCard: string) {
    restApi.addAttachmentsToCard(idTargetCard, currentCard?.url as string);
  }

  function onRemoveAttachmentRequestHandler(idAttachment: string) {
    restApi.removeAttachmentsFromCard(currentCard?.id as string, idAttachment);
  }

  return (
    <div css={styles.container}>
      {/* <div css={styles.error}>Note: <b>Bulk Attachments</b> and <b>Board Targets</b> functionality will be removed in future version of <b>Bulk Actions Power-Up</b> and all of the card attachments will be again displayed in the default <b>Trello Attachments</b> section.</div> */}
      <ConditionalRender
        condition1={errors}
        renderIf1True={
          <div css={styles.error}>
            ERROR: Not all of the cards&apos; data had been fetched. This is
            likely caused by either connectivity issues or a large amount of
            Trello Cards Attachments.
          </div>
        }
      />
      <ConditionalRender
        condition1={currentCard !== null}
        renderIf1True={
          <React.Fragment>
            <Summary
              doneCount={getTargetsStats().done}
              wipCount={getTargetsStats().wip}
              cardsWithTargetsCount={getTargetsStats().all}
              allCardsCount={
                cards.validCards.length +
                cards.removedCardsAttachments.length +
                cards.unauthorizedToViewCards.length
              }
              removedCardsCount={cards.removedCardsAttachments.length}
              unauthorizedCardsCount={cards.unauthorizedToViewCards.length}
              boardsCount={
                new Set(cards.validCards.map((c) => c.card.idBoard)).size
              }
            />
            <CardsGroupsList
              boardsWithTargets={boardTargets.map((t) => t.board.id)}
              validCards={cards.validCards}
              removedCardsAttachments={cards.removedCardsAttachments}
              unauthorizedToViewCards={cards.unauthorizedToViewCards}
              shortLinkCurrentCard={currentCard?.shortLink as string}
              onConnectCardsRequest={onConnectCardsRequestHandler}
              onRemoveAttachmentRequest={onRemoveAttachmentRequestHandler}
            />
            <div css={[styles.note]}>
              Note: You can switch to default view through &apos;Bulk Card
              Attachmnets&apos; button.
            </div>
          </React.Fragment>
        }
      />
    </div>
  );
}

export default AttachmentsOverview;

const styles = {
  container: css`
    max-height: 800px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    ${baseStyles.verticalScrollbar}
    padding-right: 4px;
  `,
  note: css`
    ${fontStylesNew.verySmallRegular}
    text-align: start;
  `,
  error: css`
    margin-bottom: 16px;
    color: ${trelloColors["red-600"]};
  `,
};
